import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useLayoutEffect,
} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SvgIcon from "@mui/material/SvgIcon";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import PanToolIcon from "@mui/icons-material/PanTool";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import CropFreeIcon from "@mui/icons-material/CropFree";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import { CameraLocationTab } from "./CameraLocationTab.tsx";
import { ZonesTab, type DrawnZone } from "./ZonesTab.tsx";
import { ZoneDrawingCanvas } from "./ZoneDrawingCanvas.tsx";
import {
  AIModelsTab,
  type CameraAiConfigItem,
  normalizeCameraAiConfigResponseBody,
  type PostCamerasCreateBody,
} from "./AIModelsTab.tsx";
import type { CameraZone } from "./CameraZonesTable";
import { fetchAndApplyRecycleBinTotals } from "../../utils/recycleBinTotalsSync.ts";
import { userTimezoneHeaders } from "../../utils/userTimezoneHeader";
import {
  AddCameraDialog,
  type AddCameraFormValues,
} from "./AddCameraDialog.tsx";
import {
  fetchCameraById,
  resolveCameraMediaUrl,
  buildStreamUrlForCameraPostPayload,
  type CameraDetail,
} from "../../services/cameraDetailApi.ts";
import { GLOBAL_TOOLTIP_PROPS } from "../../config/tooltips.config.ts";
import React from "react";
import toast from "react-hot-toast";
import {
  mapCanvasPointsToStreamCoordinates,
  mapStreamCoordinatesToCanvasPoints,
  parseZoneResolutionString,
  getIntrinsicMediaSize,
  getObjectFitContainContentRect,
} from "../../utils/zoneStreamCoordinates.ts";

declare const process: { env: Record<string, string | undefined> };
const ZONES_API_BASE = process.env.REACT_APP_API_URL_FOR_AI_DETECTION || "";

/** Maximum zones that can exist per camera (enforced when creating a new zone). */
const MAX_ZONES_PER_CAMERA = 30;

/** Same `id` replaces any existing duplicate-name toast so only one shows at a time. */
const ZONE_DUPLICATE_NAME_TOAST = {
  position: "bottom-center" as const,
  id: "zone-config-duplicate-name",
};

const ZONE_MAX_COUNT_TOAST = {
  position: "bottom-center" as const,
  id: "zone-config-max-zones",
};

const UUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
function isUuid(id: string): boolean {
  return UUID_REGEX.test(id);
}

/** Get zone id from create/update API response (supports { data: { id } } or { id }) */
function getResponseId(res: Record<string, unknown>): string | undefined {
  const data = res?.data as Record<string, unknown> | undefined;
  return (data?.id as string) ?? (res?.id as string);
}

/** GET /cameras/{camera_id}/zones response item (backend may send coordinates as array or string). */
interface ZoneByCameraItem {
  id: string;
  camera_id: string;
  name?: string;
  zone_type?: string;
  coordinates?: string | number[][];
  /** Coordinate space when stored (e.g. "600X400"). */
  resolution?: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

/** Wrapped API response: { message, status, data: ZoneByCameraItem[] }. */
interface ZonesByCameraApiResponse {
  data?: ZoneByCameraItem[];
  message?: string;
  status?: string;
}

/** Parse coordinates from API (string JSON or array of [x,y]) to points for DrawnZone. */
function parseZoneCoordinates(
  coordinates: string | number[][] | undefined,
): { x: number; y: number }[] {
  if (coordinates == null) return [];
  let arr: unknown[];
  if (typeof coordinates === "string") {
    try {
      const parsed = JSON.parse(coordinates) as unknown;
      arr = Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  } else if (Array.isArray(coordinates)) {
    arr = coordinates;
  } else {
    return [];
  }
  return arr
    .map((p) => {
      if (Array.isArray(p) && p.length >= 2)
        return { x: Number(p[0]), y: Number(p[1]) };
      if (p && typeof p === "object" && "x" in p && "y" in p)
        return {
          x: Number((p as { x: unknown; y: unknown }).x),
          y: Number((p as { x: unknown; y: unknown }).y),
        };
      return { x: 0, y: 0 };
    })
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
}

interface ZoneConfigurationPageNewProps {
  cameraZone: CameraZone;
  /**
   * When true (e.g. opened from Zone Management "View Details"), Zones and AI Models tabs
   * do not allow add / edit / delete — browse only.
   */
  readOnly?: boolean;
  onSave: (updatedZone: CameraZone) => void;
  /**
   * Invoked when the user taps the header back control, before closing.
   * Parent should run GET /cameras (e.g. refresh list / zones_count). If omitted, a direct GET /cameras is still sent when `REACT_APP_API_URL_FOR_AI_DETECTION` is set (parent state may not update without the callback).
   */
  onRefetchCamerasList?: () => void | Promise<void>;
  /** When set, header shows Add Camera (same POST flow as Manage Zones list). */
  onAddCameraApply?: (values: AddCameraFormValues) => void | Promise<void>;
}

/** Session cache: avoid duplicate GET /cameras/{id} when reopening the same camera. */
const cameraDetailSessionCache = new Map<string, CameraDetail>();

const MARK_ZONES_HELP_TOOLTIP =
  "Draw polygons or lines on the camera feed to define detection zones. Use the toolbar to zoom, pan, and toggle zone visibility. Press Enter to save a zone.";

/** Tooltips default to document.body; that layer is hidden under native fullscreen — keep popper inside the panel. */
function markZonesInfoTooltipPopperProps(isFullscreen: boolean) {
  return {
    disablePortal: isFullscreen,
    sx: { zIndex: 1600 },
  } as const;
}

export function ZoneConfigurationPageNew({
  cameraZone,
  readOnly = false,
  onSave,
  onRefetchCamerasList,
  onAddCameraApply,
}: ZoneConfigurationPageNewProps) {
  /** Camera UUID for API paths — zone-management rows may use zone_id as row id. */
  const effectiveCameraId = cameraZone.cameraApiId ?? cameraZone.id;

  const [cameraDetail, setCameraDetail] = useState<CameraDetail | null>(null);
  const [cameraDetailLoading, setCameraDetailLoading] = useState(false);
  const [cameraDetailError, setCameraDetailError] = useState<string | null>(
    null,
  );
  const cameraDetailAbortRef = useRef<AbortController | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  /** Snapshot image under Mark Zones (natural size for coordinate mapping). */
  const previewImgRef = useRef<HTMLImageElement | null>(null);
  /** Wrapper that contains img/video + ZoneDrawingCanvas (same box as CSS `object-fit` media). */
  const markZonesContentRef = useRef<HTMLDivElement | null>(null);
  /** Last GET /cameras/{id}/zones list — remapped when video/image intrinsic size or layout updates. */
  const zonesApiSnapshotRef = useRef<ZoneByCameraItem[]>([]);
  /** Native decoded dimensions for stream / preview (for API resolution + scaling). */
  const [markZonesStreamSize, setMarkZonesStreamSize] = useState({
    w: 0,
    h: 0,
  });
  const [streamError, setStreamError] = useState<string | null>(null);
  const [previewImageLoadFailed, setPreviewImageLoadFailed] = useState(false);
  const [addCameraOpen, setAddCameraOpen] = useState(false);
  const [zoneConfigSettingsOpen, setZoneConfigSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: Camera Location, 1: Zones, 2: AI Models
  /** True while Mark Zones shows Undo/Redo/Cancel (polygon draft before complete). */
  const [markZonesDraftToolbarActive, setMarkZonesDraftToolbarActive] =
    useState(false);
  const [showZones, setShowZones] = useState(true);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawnZones, setDrawnZones] = useState<DrawnZone[]>([]);
  const [editingZone, setEditingZone] = useState<DrawnZone | null>(null);
  /** Zones tab row click — highlight matching shape on Mark Zones (separate from edit mode). */
  const [markZonesHighlightedZoneId, setMarkZonesHighlightedZoneId] = useState<
    string | null
  >(null);
  const [cameraAiConfigs, setCameraAiConfigs] = useState<CameraAiConfigItem[]>(
    [],
  );
  const [cameraAiConfigsLoading, setCameraAiConfigsLoading] = useState(false);
  const [cameraAiConfigsError, setCameraAiConfigsError] = useState<
    string | null
  >(null);
  const [configsRefreshKey, setConfigsRefreshKey] = useState(0);
  const [zonesByCameraLoading, setZonesByCameraLoading] = useState(false);
  const [zonesByCameraError, setZonesByCameraError] = useState<string | null>(
    null,
  );
  const [markZoneZoom, setMarkZoneZoom] = useState(100);
  const [isMarkZoneFullscreen, setIsMarkZoneFullscreen] = useState(false);
  const markZonePanelRef = useRef<HTMLDivElement>(null);
  /** Portal target for Undo/Redo/Cancel + instruction (outside CSS zoom so they stay visible). */
  const [markZonesDraftOverlayEl, setMarkZonesDraftOverlayEl] =
    useState<HTMLDivElement | null>(null);
  /** Tracked so zone overlays are re-mapped when container aspect ratio changes on resize. */
  const [markZonesContainerSize, setMarkZonesContainerSize] = useState({
    w: 0,
    h: 0,
  });

  /** Same inset as `object-fit: contain` on the preview — drawing only on the image, not letterboxing. */
  const markZonesDrawingContentRect = useMemo(() => {
    const cw = markZonesContainerSize.w;
    const ch = markZonesContainerSize.h;
    const iw = markZonesStreamSize.w;
    const ih = markZonesStreamSize.h;
    if (cw <= 0 || ch <= 0) {
      return { offsetX: 0, offsetY: 0, contentW: 0, contentH: 0 };
    }
    if (iw <= 0 || ih <= 0) {
      return { offsetX: 0, offsetY: 0, contentW: cw, contentH: ch };
    }
    return getObjectFitContainContentRect(cw, ch, iw, ih);
  }, [
    markZonesContainerSize.w,
    markZonesContainerSize.h,
    markZonesStreamSize.w,
    markZonesStreamSize.h,
  ]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const el =
        document.fullscreenElement ??
        (document as unknown as { webkitFullscreenElement?: Element })
          .webkitFullscreenElement;
      setIsMarkZoneFullscreen(!!el && el === markZonePanelRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
    };
  }, []);

  useEffect(() => {
    const el = markZonesContentRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setMarkZonesContainerSize((prev) => {
          const w = Math.round(width);
          const h = Math.round(height);
          if (prev.w === w && prev.h === h) return prev;
          return { w, h };
        });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleMarkZoneFullscreen = () => {
    const el = markZonePanelRef.current;
    if (!el) return;
    if (isMarkZoneFullscreen) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (
        (document as unknown as { webkitExitFullscreen?: () => void })
          .webkitExitFullscreen
      )
        (
          document as unknown as { webkitExitFullscreen: () => void }
        ).webkitExitFullscreen();
    } else {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (
        (el as unknown as { webkitRequestFullscreen?: () => void })
          .webkitRequestFullscreen
      )
        (
          el as unknown as { webkitRequestFullscreen: () => void }
        ).webkitRequestFullscreen();
    }
  };

  const handleMarkZoneZoomIn = () =>
    setMarkZoneZoom((z) => Math.min(200, z + 10));
  const handleMarkZoneZoomOut = () =>
    setMarkZoneZoom((z) => Math.max(50, z - 10));

  useEffect(() => {
    if (!readOnly) return;
    setIsDrawingMode(false);
    setEditingZone(null);
  }, [readOnly]);

  useEffect(() => {
    setMarkZonesHighlightedZoneId(null);
  }, [effectiveCameraId]);

  useEffect(() => {
    if (isDrawingMode && !editingZone) {
      setMarkZonesHighlightedZoneId(null);
    }
  }, [isDrawingMode, editingZone]);

  /** GET /cameras/{camera_id} when opening zone config (same entry as table double-click, zone link, menu Edit/View). */
  useEffect(() => {
    const base = ZONES_API_BASE.replace(/\/$/, "");
    const id = effectiveCameraId;

    cameraDetailAbortRef.current?.abort();
    cameraDetailAbortRef.current = null;

    if (!base || !id || !isUuid(id)) {
      setCameraDetail(null);
      setCameraDetailLoading(false);
      setCameraDetailError(
        base && id && !isUuid(id)
          ? "Camera id is not a valid UUID; cannot load camera details."
          : null,
      );
      return;
    }

    const cached = cameraDetailSessionCache.get(id);
    if (cached) {
      setCameraDetail(cached);
      setCameraDetailLoading(false);
      setCameraDetailError(null);
      return;
    }

    const ac = new AbortController();
    cameraDetailAbortRef.current = ac;
    setCameraDetailLoading(true);
    setCameraDetailError(null);
    setCameraDetail(null);

    fetchCameraById(ZONES_API_BASE, id, ac.signal)
      .then((detail) => {
        cameraDetailSessionCache.set(id, detail);
        setCameraDetail(detail);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === "AbortError") return;
        const message =
          err instanceof Error ? err.message : "Failed to load camera";
        console.error(
          "[ZoneConfigurationPageNew] GET /cameras/{id} failed",
          message,
        );
        setCameraDetailError(message);
        setCameraDetail(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) {
          setCameraDetailLoading(false);
          cameraDetailAbortRef.current = null;
        }
      });

    return () => {
      ac.abort();
    };
  }, [effectiveCameraId]);

  const mergedStreamUrl = useMemo(() => {
    const fromDetail = cameraDetail?.stream_url?.trim();
    if (fromDetail) return fromDetail;
    return (cameraZone?.cameraIp && String(cameraZone.cameraIp).trim()) || "";
  }, [cameraDetail?.stream_url, cameraZone.cameraIp]);

  const mergedCameraName = useMemo(
    () =>
      (cameraDetail?.name && cameraDetail.name.trim()) ||
      cameraZone.cameraName ||
      "",
    [cameraDetail?.name, cameraZone.cameraName],
  );

  const previewImageUrl = useMemo(() => {
    if (!cameraDetail?.preview_url) return "";
    return resolveCameraMediaUrl(ZONES_API_BASE, cameraDetail.preview_url);
  }, [cameraDetail?.preview_url]);

  useEffect(() => {
    setPreviewImageLoadFailed(false);
  }, [previewImageUrl]);

  /** Browsers cannot play RTSP in <video>; use preview image from GET /cameras/{id} for Mark Zones instead. */
  const streamLikelyPlayableInBrowser = useMemo(() => {
    const u = mergedStreamUrl.trim();
    if (!u) return false;
    return !/^rtsp:\/\//i.test(u);
  }, [mergedStreamUrl]);

  /**
   * Mark Zones needs a working preview image or a browser-playable stream that actually loads.
   * If only a video URL is used and the video errors (e.g. unsupported), Add Zone must stay off.
   */
  const markZonesSurfaceReady = useMemo(() => {
    if (cameraDetailLoading) return false;
    if (previewImageUrl && !previewImageLoadFailed) return true;
    const stream = mergedStreamUrl.trim();
    if (!stream || !streamLikelyPlayableInBrowser) return false;
    if (streamError) return false;
    return true;
  }, [
    cameraDetailLoading,
    previewImageUrl,
    previewImageLoadFailed,
    mergedStreamUrl,
    streamLikelyPlayableInBrowser,
    streamError,
  ]);

  /**
   * Orange pill "· N zone(s)": GET /cameras/{id} `zones_count` can stay stale after POST /zones.
   * For the open camera, never show a count below the live list from GET /cameras/{id}/zones (`drawnZones`).
   */
  const mergedZonesCount = useMemo(() => {
    const apiCount =
      typeof cameraDetail?.zones_count === "number" &&
      !Number.isNaN(cameraDetail.zones_count)
        ? cameraDetail.zones_count
        : null;
    const rowCount =
      typeof cameraZone.zone === "number" && !Number.isNaN(cameraZone.zone)
        ? cameraZone.zone
        : null;
    const baseline = apiCount ?? rowCount ?? 0;

    if (!effectiveCameraId) {
      if (apiCount != null) return apiCount;
      return rowCount;
    }
    return Math.max(baseline, drawnZones.length);
  }, [
    cameraDetail?.zones_count,
    cameraZone.zone,
    drawnZones.length,
    effectiveCameraId,
  ]);

  /**
   * POST /cameras before AI Models Save only when the row has no backend camera id yet.
   * Existing cameras (UUID) must not hit POST /cameras on AI save — that duplicates create and returns 422.
   */
  const cameraSyncPayloadForAiTab =
    useMemo((): PostCamerasCreateBody | null => {
      if (effectiveCameraId && isUuid(effectiveCameraId)) {
        return null;
      }
      const name =
        (cameraDetail?.name && cameraDetail.name.trim()) ||
        (cameraZone.cameraName && cameraZone.cameraName.trim()) ||
        "Camera";
      const stream_url = buildStreamUrlForCameraPostPayload(
        cameraDetail,
        cameraZone.cameraIp || "",
      );
      return { name, stream_url };
    }, [cameraDetail, cameraZone, effectiveCameraId]);

  // Live stream / Mark Zones video — uses merged URL from GET /cameras/{id} when available
  const streamUrl = mergedStreamUrl;

  /** API zone rows → DrawnZone points in 600×400 canvas space (inverse of stream pixel + `object-fit: contain`). */
  const mapApiItemsToDrawnZones = useCallback(
    (list: ZoneByCameraItem[]): DrawnZone[] => {
      const containerEl = markZonesContentRef.current;
      const rect = containerEl?.getBoundingClientRect() ?? null;
      const mediaEl =
        streamUrl && streamLikelyPlayableInBrowser && !streamError
          ? videoRef.current
          : previewImgRef.current;
      let iw = getIntrinsicMediaSize(mediaEl).width;
      let ih = getIntrinsicMediaSize(mediaEl).height;
      if (iw <= 0) iw = markZonesStreamSize.w;
      if (ih <= 0) ih = markZonesStreamSize.h;

      return list.map((z) => {
        const rawPoints = parseZoneCoordinates(z.coordinates);
        const res = parseZoneResolutionString(z.resolution);
        const canvasPoints = mapStreamCoordinatesToCanvasPoints(
          rawPoints,
          res,
          rect,
          iw,
          ih,
        );
        return {
          id: z.id,
          name: z.name?.trim() ? z.name.trim() : `Zone ${z.id.slice(0, 8)}`,
          type: (z.zone_type === "line" ? "line" : "polygon") as
            | "polygon"
            | "line",
          points: canvasPoints,
          color: "#FF8555",
          isActive: true,
          createdAt: z.created_at ? new Date(z.created_at) : new Date(),
        };
      });
    },
    [
      streamUrl,
      streamLikelyPlayableInBrowser,
      streamError,
      markZonesStreamSize.w,
      markZonesStreamSize.h,
    ],
  );

  useLayoutEffect(() => {
    if (zonesApiSnapshotRef.current.length === 0) return;
    if (activeTab !== 1) return;
    setDrawnZones(mapApiItemsToDrawnZones(zonesApiSnapshotRef.current));
  }, [
    activeTab,
    markZonesStreamSize.w,
    markZonesStreamSize.h,
    markZoneZoom,
    mapApiItemsToDrawnZones,
    markZonesContainerSize.w,
    markZonesContainerSize.h,
  ]);

  useEffect(() => {
    setStreamError(null);
    const video = videoRef.current;
    if (!video || !streamUrl || !streamLikelyPlayableInBrowser) return;
    video.src = streamUrl;
    video.load();
    const onCanPlay = () => setStreamError(null);
    const onError = () =>
      setStreamError("Stream unavailable or unsupported in browser");
    video.addEventListener("canplay", onCanPlay);
    video.addEventListener("error", onError);
    return () => {
      video.removeEventListener("canplay", onCanPlay);
      video.removeEventListener("error", onError);
      video.src = "";
    };
  }, [streamUrl, streamLikelyPlayableInBrowser]);

  useEffect(() => {
    if (!markZonesSurfaceReady && isDrawingMode) {
      setIsDrawingMode(false);
      setEditingZone(null);
    }
  }, [markZonesSurfaceReady, isDrawingMode]);

  /** GET /cameras/{camera_id}/zones — Zones tab + AI Models zone dropdown. */
  const fetchZonesForCamera = useCallback(
    async (signal?: AbortSignal) => {
      const base = ZONES_API_BASE.replace(/\/$/, "");
      const cameraId = effectiveCameraId;
      if (!base || !cameraId) return;
      setZonesByCameraLoading(true);
      setZonesByCameraError(null);
      try {
        const url = `${base}/cameras/${encodeURIComponent(cameraId)}/zones`;
        const res = await fetch(url, {
          signal,
          headers: { Accept: "application/json", ...userTimezoneHeaders() },
        });
        if (!res.ok) {
          throw new Error(res.statusText || "Failed to load zones");
        }
        const body = (await res.json()) as
          | ZonesByCameraApiResponse
          | ZoneByCameraItem[];
        if (signal?.aborted) return;
        const list = Array.isArray(body)
          ? body
          : ((Array.isArray(body.data) ? body.data : []) ?? []);
        const active = list.filter(
          (z) =>
            z?.id &&
            (z.deleted_at == null || String(z.deleted_at).trim() === ""),
        );
        zonesApiSnapshotRef.current = active;
        setDrawnZones(mapApiItemsToDrawnZones(active));
      } catch (err) {
        if (
          signal?.aborted ||
          (err instanceof DOMException && err.name === "AbortError")
        ) {
          return;
        }
        setZonesByCameraError(
          err instanceof Error ? err.message : "Failed to load zones",
        );
        zonesApiSnapshotRef.current = [];
        setDrawnZones([]);
      } finally {
        setZonesByCameraLoading(false);
      }
    },
    [effectiveCameraId, mapApiItemsToDrawnZones],
  );

  useEffect(() => {
    if (activeTab !== 1 && activeTab !== 2) return;
    const ac = new AbortController();
    void fetchZonesForCamera(ac.signal);
    return () => ac.abort();
  }, [activeTab, effectiveCameraId, fetchZonesForCamera]);

  // Fetch GET /camera-ai-configs when user opens the AI Models tab in Zone Configuration
  useEffect(() => {
    if (activeTab !== 2) return;
    const base = ZONES_API_BASE.replace(/\/$/, "");
    if (!base) return;

    const cameraId = effectiveCameraId;
    const url = cameraId
      ? `${base}/camera-ai-configs?camera_id=${encodeURIComponent(cameraId)}`
      : `${base}/camera-ai-configs`;

    let cancelled = false;
    setCameraAiConfigsLoading(true);
    setCameraAiConfigsError(null);
    fetch(url, {
      headers: { Accept: "application/json", ...userTimezoneHeaders() },
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((raw: unknown) => {
        if (!cancelled) {
          const list = normalizeCameraAiConfigResponseBody(raw);
          setCameraAiConfigs(list);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setCameraAiConfigsError(
            err instanceof Error ? err.message : "Failed to load configs",
          );
          setCameraAiConfigs([]);
        }
      })
      .finally(() => {
        if (!cancelled) setCameraAiConfigsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, effectiveCameraId, configsRefreshKey]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (markZonesDraftToolbarActive) return;
    setActiveTab(newValue);
  };

  const handleZoneSaved = async (zone: DrawnZone): Promise<boolean> => {
    const trimmedName = zone.name.trim();
    const nameKey = trimmedName.toLowerCase();
    const excludeId = editingZone?.id;
    const duplicateName = drawnZones.some(
      (z) => z.id !== excludeId && z.name.trim().toLowerCase() === nameKey,
    );
    if (duplicateName) {
      toast.error(
        "A zone with this name already exists.",
        ZONE_DUPLICATE_NAME_TOAST,
      );
      return false;
    }

    const isNewZone = !editingZone;
    if (isNewZone && drawnZones.length >= MAX_ZONES_PER_CAMERA) {
      toast.error(
        `This camera already has the maximum of ${MAX_ZONES_PER_CAMERA} zones.`,
        ZONE_MAX_COUNT_TOAST,
      );
      return false;
    }

    const base = ZONES_API_BASE.replace(/\/$/, "");
    const containerEl = markZonesContentRef.current;
    const rect = containerEl?.getBoundingClientRect() ?? null;
    const mediaEl =
      streamUrl && streamLikelyPlayableInBrowser && !streamError
        ? videoRef.current
        : previewImgRef.current;
    let iw = getIntrinsicMediaSize(mediaEl).width;
    let ih = getIntrinsicMediaSize(mediaEl).height;
    if (iw <= 0) iw = markZonesStreamSize.w;
    if (ih <= 0) ih = markZonesStreamSize.h;
    const intrinsicW = iw > 0 ? iw : 1920;
    const intrinsicH = ih > 0 ? ih : 1080;

    const coordinates = mapCanvasPointsToStreamCoordinates(
      zone.points,
      rect,
      intrinsicW,
      intrinsicH,
    );
    const resolutionStr = `${Math.round(intrinsicW)}X${Math.round(intrinsicH)}`;

    if (process.env.NODE_ENV === "development") {
      const firstCanvas = zone.points[0];
      const firstStream = coordinates[0];
      console.log("[ZoneConfig] zone save coordinate mapping", {
        canvasFirst: firstCanvas,
        streamFirst: firstStream,
        intrinsic: { w: intrinsicW, h: intrinsicH },
        container:
          rect && { width: rect.width, height: rect.height },
      });
    }

    const isEdit = Boolean(editingZone);
    const cameraId = effectiveCameraId ?? "";

    if (!base) {
      if (editingZone) {
        setDrawnZones(
          drawnZones.map((z) => (z.id === editingZone.id ? zone : z)),
        );
        setEditingZone(null);
      } else {
        setDrawnZones([...drawnZones, zone]);
      }
      setIsDrawingMode(false);
      return true;
    }

    /** Payload: coordinates in native stream / image pixel space; resolution matches that space. */
    const zonePayload = {
      camera_id: cameraId,
      name: trimmedName,
      zone_type: zone.type,
      coordinates,
      resolution: resolutionStr,
    };

    try {
      if (isEdit && editingZone) {
        if (isUuid(editingZone.id)) {
          const putUrl = `${base}/zones/${encodeURIComponent(editingZone.id)}`;
          const res = await fetch(putUrl, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              ...userTimezoneHeaders(),
            },
            body: JSON.stringify(zonePayload),
          });
          if (res.status === 200) {
            const updated = await res.json().catch(() => ({}));
            const zoneWithId = {
              ...zone,
              id: getResponseId(updated || {}) ?? zone.id,
            };
            setDrawnZones(
              drawnZones.map((z) => (z.id === editingZone.id ? zoneWithId : z)),
            );
            setEditingZone(null);
          } else {
            setDrawnZones(
              drawnZones.map((z) => (z.id === editingZone.id ? zone : z)),
            );
            setEditingZone(null);
          }
        } else {
          const postUrl = `${base}/zones`;
          const res = await fetch(postUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...userTimezoneHeaders(),
            },
            body: JSON.stringify(zonePayload),
          });
          if (res.status === 201) {
            const created = await res.json().catch(() => ({}));
            const zoneWithId = {
              ...zone,
              id: getResponseId(created || {}) ?? zone.id,
            };
            setDrawnZones(
              drawnZones.map((z) => (z.id === editingZone.id ? zoneWithId : z)),
            );
            setEditingZone(null);
          } else {
            setDrawnZones(
              drawnZones.map((z) => (z.id === editingZone.id ? zone : z)),
            );
            setEditingZone(null);
          }
        }
      } else {
        const postUrl = `${base}/zones`;
        const res = await fetch(postUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...userTimezoneHeaders(),
          },
          body: JSON.stringify(zonePayload),
        });
        if (res.status === 201) {
          const created = await res.json().catch(() => ({}));
          const zoneWithId = {
            ...zone,
            id: getResponseId(created || {}) ?? zone.id,
          };
          setDrawnZones([...drawnZones, zoneWithId]);
        } else {
          setDrawnZones([...drawnZones, zone]);
        }
      }
    } catch {
      if (editingZone) {
        setDrawnZones(
          drawnZones.map((z) => (z.id === editingZone.id ? zone : z)),
        );
        setEditingZone(null);
      } else {
        setDrawnZones([...drawnZones, zone]);
      }
    }
    setIsDrawingMode(false);
    return true;
  };

  const handleCancelDrawing = () => {
    setIsDrawingMode(false);
    setEditingZone(null);
    setMarkZonesHighlightedZoneId(null);
  };

  const handleEditZone = (zone: DrawnZone) => {
    setEditingZone(zone);
    setMarkZonesHighlightedZoneId(zone.id);
    setIsDrawingMode(true);
  };

  const handleDeleteZone = async (zoneId: string) => {
    const base = ZONES_API_BASE.replace(/\/$/, "");
    const clearHighlightIfDeleted = () => {
      setMarkZonesHighlightedZoneId((prev) =>
        prev === zoneId ? null : prev,
      );
    };
    if (!base) {
      setDrawnZones((prev) => prev.filter((z) => z.id !== zoneId));
      clearHighlightIfDeleted();
      return;
    }
    try {
      await fetch(`${base}/zones/${encodeURIComponent(zoneId)}`, {
        method: "DELETE",
        headers: { Accept: "application/json", ...userTimezoneHeaders() },
      });
      void fetchAndApplyRecycleBinTotals();
    } finally {
      setDrawnZones((prev) => prev.filter((z) => z.id !== zoneId));
      clearHighlightIfDeleted();
    }
  };

  /** GET /cameras when leaving via the header back control (then close). */
  const exitAfterRefetchingCameras = useCallback(async () => {
    const base = ZONES_API_BASE.replace(/\/$/, "");
    try {
      if (onRefetchCamerasList) {
        await Promise.resolve(onRefetchCamerasList());
      } else if (base) {
        const res = await fetch(`${base}/cameras`, {
          method: "GET",
          headers: { Accept: "application/json", ...userTimezoneHeaders() },
        });
        if (!res.ok) {
          console.error(
            "[ZoneConfigurationPageNew] GET /cameras before exit failed",
            res.status,
          );
        }
      }
    } catch (err) {
      console.error(
        "[ZoneConfigurationPageNew] GET /cameras before exit failed",
        err,
      );
    }
    onSave(cameraZone);
  }, [cameraZone, onRefetchCamerasList, onSave]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#F2F2F2",
        overflow: "hidden",
      }}
    >
      {/* Header: ← Title (left) | spacer | settings (right) */}
      <Box
        sx={{
          height: 56,
          bgcolor: "white",
          borderBottom: "1px solid #E5E7EB",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          px: 2,
          gap: 1.5,
          flexWrap: "nowrap",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
            flexShrink: 1,
          }}
        >
          <IconButton
            onClick={() => void exitAfterRefetchingCameras()}
            disabled={markZonesDraftToolbarActive}
            size="small"
            title={
              markZonesDraftToolbarActive
                ? "Finish or cancel drawing the zone before leaving"
                : undefined
            }
            sx={{ color: "#4A5565", p: 0.5, flexShrink: 0 }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              fontWeight: 500,
              color: "#212121",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Zone Configuration
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 16 }} aria-hidden />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {/* {onAddCameraApply ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddCameraOpen(true)}
              aria-label="Add camera"
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                fontWeight: 600,
                bgcolor: "#2932E5",
                borderRadius: "10px",
                height: 36,
                px: 2,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#1e28b8",
                  boxShadow: "none",
                },
              }}
            >
              Add Camera
            </Button>
          ) : null} */}
          <Tooltip
            title="Zone Configuration settings"
            placement="bottom"
            {...GLOBAL_TOOLTIP_PROPS}
          >
            <IconButton
              size="small"
              onClick={() => setZoneConfigSettingsOpen(true)}
              aria-label="Open Zone Configuration settings"
              sx={{
                flexShrink: 0,
                // border: "1px solid #E5E7EB",
                // borderRadius: "10px",
                width: 36,
                height: 36,
                color: "#4A5565",
                "&:hover": {
                  bgcolor: "#f5f5f5",
                  color: "#2932E5",
                },
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {onAddCameraApply ? (
        <AddCameraDialog
          open={addCameraOpen}
          onClose={() => setAddCameraOpen(false)}
          onApply={onAddCameraApply}
        />
      ) : null}

      <Dialog
        open={zoneConfigSettingsOpen}
        onClose={() => setZoneConfigSettingsOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="zone-configuration-settings-title"
      >
        <DialogTitle
          id="zone-configuration-settings-title"
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: 18,
            fontWeight: 600,
            color: "#000000",
          }}
        >
          Settings
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              color: "#212121",
            }}
          >
            Upcoming feature
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, justifyContent: "flex-end" }}>
          <Button
            onClick={() => setZoneConfigSettingsOpen(false)}
            variant="contained"
            color="primary"
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 500,
              borderRadius: 1,
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Content: Split Panel */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          gap: 1,
          p: 1,
          overflow: "hidden",
        }}
      >
        {/* LEFT PANEL: Mark Zones (screenshot layout) */}
        <Paper
          ref={markZonePanelRef}
          sx={{
            minWidth: 420,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 1.5,
            borderRadius: "10px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            overflow: "hidden",
            ...(isMarkZoneFullscreen && {
              minHeight: "100vh",
              height: "100vh",
              width: "100%",
              maxWidth: "100vw",
              borderRadius: 0,
              // Allow non-portaled tooltips to paint above the video stack (still clipped by inner black box).
              overflow: "visible",
            }),
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <Typography
              component="div"
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                fontWeight: 600,
                color: "#FF8555",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              Mark Zones
              <Tooltip
                title={MARK_ZONES_HELP_TOOLTIP}
                placement="bottom-start"
                arrow
                enterDelay={300}
                PopperProps={markZonesInfoTooltipPopperProps(
                  isMarkZoneFullscreen,
                )}
              >
                <IconButton
                  size="small"
                  sx={{ p: 0, flexShrink: 0 }}
                  aria-label="Mark Zones info"
                >
                  <InfoIcon sx={{ fontSize: 18, color: "#4A5565" }} />
                </IconButton>
              </Tooltip>
            </Typography>
            {/* Camera Group | Name — pill tag (no camera IP) */}
            <Box
              sx={{
                bgcolor: "#FFEDE0",
                color: "#424242",
                border: "1px solid #E85D2C",
                px: 1.5,
                py: 0.75,
                borderRadius: "50px",
                fontFamily: "Roboto, sans-serif",
                fontSize: 13,
                fontWeight: 500,
                flexShrink: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {cameraZone.cameraGroup || "—"} | {mergedCameraName.trim() || "—"}
              {typeof mergedZonesCount === "number"
                ? ` · ${mergedZonesCount} zone(s)`
                : ""}
            </Box>
          </Box>

          {cameraDetailError && (
            <Alert severity="error" sx={{ mb: 1, py: 0 }}>
              {cameraDetailError}
            </Alert>
          )}

          {/* Black canvas – Camera Feed with toolbar: Zoom (left), Fullscreen + Hide Zones (right) */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "#000",
              borderRadius: "8px",
              position: "relative",
              overflow: "hidden",
              minHeight: 280,
              minWidth: 0,
              ...(isMarkZoneFullscreen && { flex: 1, minHeight: 0 }),
            }}
          >
            {/* Toolbar: Zoom In/Out (left), Info (fullscreen) + Fullscreen + Hide Zones (right) — z-index above scaled canvas stack */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 50,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                p: 1,
                pointerEvents: "none",
                "& .MuiIconButton-root, & .MuiButtonBase-root, & [role='button']":
                  {
                    pointerEvents: "auto",
                  },
                "& .MuiTooltip-popper, & .MuiTooltip-tooltip": {
                  pointerEvents: "auto",
                },
                background:
                  "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={handleMarkZoneZoomOut}
                  disabled={markZoneZoom <= 50}
                  sx={{ color: "#fff", p: 0.5 }}
                  aria-label="Zoom out"
                >
                  <ZoomOutIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 13,
                    color: "#fff",
                    minWidth: 40,
                    textAlign: "center",
                  }}
                >
                  {markZoneZoom}%
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleMarkZoneZoomIn}
                  disabled={markZoneZoom >= 200}
                  sx={{ color: "#fff", p: 0.5 }}
                  aria-label="Zoom in"
                >
                  <ZoomInIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pointerEvents: "auto",
                }}
              >
                {isMarkZoneFullscreen && (
                  <Tooltip
                    title={MARK_ZONES_HELP_TOOLTIP}
                    placement="bottom-end"
                    arrow
                    enterDelay={300}
                    PopperProps={markZonesInfoTooltipPopperProps(true)}
                  >
                    <IconButton
                      size="small"
                      sx={{ color: "#fff", p: 0.5 }}
                      aria-label="Mark Zones info"
                    >
                      <InfoIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  size="small"
                  onClick={handleMarkZoneFullscreen}
                  sx={{ color: "#fff", p: 0.5 }}
                  aria-label={
                    isMarkZoneFullscreen ? "Exit fullscreen" : "Fullscreen"
                  }
                >
                  {isMarkZoneFullscreen ? (
                    <FullscreenExitIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <FullscreenIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
                <Box
                  onClick={() => setShowZones((prev) => !prev)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowZones((prev) => !prev);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexDirection: showZones ? "row" : "row-reverse",
                    bgcolor: showZones ? "#D32F2F" : "#2E7D32",
                    color: "#fff",
                    px: 1.25,
                    py: 0.625,
                    borderRadius: "50px",
                    cursor: "pointer",
                    minHeight: 32,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    transition:
                      "background-color 0.2s ease, flex-direction 0.2s ease",
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      bgcolor: "#fff",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#fff",
                      letterSpacing: "0.01em",
                    }}
                  >
                    Hide Zones
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Zoomable content — below top toolbar (stacking + transform create isolated layers in some browsers) */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <Box
                ref={markZonesContentRef}
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transform: `scale(${markZoneZoom / 100})`,
                  transformOrigin: "center center",
                }}
              >
                {cameraDetailLoading && isUuid(effectiveCameraId) && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(0,0,0,0.5)",
                      zIndex: 5,
                    }}
                  >
                    <CircularProgress size={40} sx={{ color: "#fff" }} />
                  </Box>
                )}
                {/* GET /cameras/{id} preview_url — JPEG snapshot; primary background for marking zones (RTSP does not play in browser). */}
                {previewImageUrl ? (
                  <Box
                    component="img"
                    ref={previewImgRef}
                    src={previewImageUrl}
                    alt=""
                    onError={() => setPreviewImageLoadFailed(true)}
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                        setMarkZonesStreamSize({
                          w: img.naturalWidth,
                          h: img.naturalHeight,
                        });
                      }
                    }}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      zIndex: 0,
                      userSelect: "none",
                      pointerEvents: "none",
                    }}
                  />
                ) : null}
                {streamUrl && streamLikelyPlayableInBrowser ? (
                  <>
                    <video
                      ref={videoRef}
                      src={streamUrl}
                      autoPlay
                      muted
                      playsInline
                      loop
                      onLoadedMetadata={() => {
                        const v = videoRef.current;
                        if (
                          v &&
                          v.videoWidth > 0 &&
                          v.videoHeight > 0
                        ) {
                          setMarkZonesStreamSize({
                            w: v.videoWidth,
                            h: v.videoHeight,
                          });
                        }
                      }}
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        zIndex: 1,
                      }}
                    />
                    {streamError && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1.25,
                          color: "rgba(255,255,255,0.9)",
                          pointerEvents: "none",
                          zIndex: 1,
                          px: 2,
                          textAlign: "center",
                          bgcolor: "rgba(0,0,0,0.45)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 16,
                            fontWeight: 600,
                            color: "inherit",
                          }}
                        >
                          Cannot create zone
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 13,
                            fontWeight: 400,
                            color: "rgba(255,255,255,0.75)",
                            maxWidth: 360,
                          }}
                        >
                          {streamError}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : null}
                {!cameraDetailLoading &&
                  !markZonesSurfaceReady &&
                  !(
                    streamUrl &&
                    streamLikelyPlayableInBrowser &&
                    streamError
                  ) && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "rgba(255,255,255,0.85)",
                        pointerEvents: "none",
                        zIndex: 0,
                        gap: 1.25,
                        px: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: 16,
                          fontWeight: 600,
                          color: "inherit",
                        }}
                      >
                        Cannot create zone
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: 14,
                          fontWeight: 400,
                          color: "rgba(255,255,255,0.7)",
                          maxWidth: 360,
                        }}
                      >
                        {streamUrl && !streamLikelyPlayableInBrowser
                          ? "RTSP stream cannot play in the browser. Add preview_url from the camera API to show a snapshot here."
                          : "You cannot create a zone without a preview image or a working stream in Mark Zones."}
                      </Typography>
                    </Box>
                  )}

                {(isDrawingMode || drawnZones.length > 0) && showZones && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: markZonesDrawingContentRect.offsetX,
                      top: markZonesDrawingContentRect.offsetY,
                      width: markZonesDrawingContentRect.contentW,
                      height: markZonesDrawingContentRect.contentH,
                      zIndex: 2,
                      boxSizing: "border-box",
                    }}
                  >
                    <ZoneDrawingCanvas
                      isDrawing={isDrawingMode}
                      onZoneSaved={handleZoneSaved}
                      onCancelDrawing={handleCancelDrawing}
                      existingZones={
                        editingZone
                          ? drawnZones.filter((z) => z.id !== editingZone.id)
                          : drawnZones
                      }
                      editingZone={editingZone}
                      onDraftToolbarActiveChange={setMarkZonesDraftToolbarActive}
                      draftControlsPortalEl={markZonesDraftOverlayEl}
                      highlightedZoneId={
                        editingZone?.id ?? markZonesHighlightedZoneId
                      }
                    />
                  </Box>
                )}
              </Box>
            </Box>
            <Box
              ref={setMarkZonesDraftOverlayEl}
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 40,
                pointerEvents: "none",
              }}
            />
          </Box>
        </Paper>

        {/* RIGHT PANEL: Camera Location | Zones | AI Models (with icons) + Toolbar + Plan */}
        <Paper
          sx={{
            flex: 1,
            minWidth: 380,
            display: "flex",
            flexDirection: "column",
            borderRadius: "10px",
            overflow: "hidden",
            bgcolor: "white",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 48,
              borderBottom: "1px solid #E5E7EB",
              "& .MuiTabs-indicator": { bgcolor: "#2932E5", height: 2 },
              "& .MuiTab-iconWrapper": { mr: 0.75 },
            }}
          >
            <Tab
              disabled={markZonesDraftToolbarActive}
              icon={<CameraAltIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Camera Location"
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                minHeight: 48,
                color: "#212121",
                "&.Mui-selected": { color: "#2932E5" },
              }}
            />
            <Tab
              disabled={markZonesDraftToolbarActive}
              icon={
                <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 18 }}>
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    d="M5 5h14v14H5z"
                  />
                  <path
                    fill="currentColor"
                    d="M5 5m-1.25 0a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 1 1-2.5 0zM19 5m-1.25 0a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 1 1-2.5 0zM19 19m-1.25 0a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 1 1-2.5 0zM5 19m-1.25 0a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 1 1-2.5 0z"
                  />
                </SvgIcon>
              }
              iconPosition="start"
              label="Zones"
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                minHeight: 48,
                color: "#212121",
                "&.Mui-selected": { color: "#2932E5" },
              }}
            />
            <Tab
              disabled={markZonesDraftToolbarActive}
              icon={
                <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 18 }}>
                  <path
                    fill="currentColor"
                    d="M21.3353 12.0795L17.25 10.0357V5.25C17.2499 5.11075 17.2111 4.97428 17.1378 4.85586C17.0646 4.73743 16.9598 4.64174 16.8353 4.5795L12.3353 2.3295C12.2312 2.27748 12.1164 2.2504 12 2.2504C11.8836 2.2504 11.7689 2.27748 11.6648 2.3295L7.16475 4.5795C7.04019 4.64174 6.93542 4.73743 6.86216 4.85586C6.78891 4.97428 6.75007 5.11075 6.75 5.25V10.0365L2.66475 12.0787C2.54008 12.141 2.43523 12.2369 2.36197 12.3554C2.28871 12.474 2.24994 12.6106 2.25 12.75V18C2.24994 18.1394 2.28871 18.276 2.36197 18.3946C2.43523 18.5131 2.54008 18.609 2.66475 18.6713L7.16475 20.9213C7.26885 20.9733 7.38363 21.0003 7.5 21.0003C7.61637 21.0003 7.73115 20.9733 7.83525 20.9213L12 18.8385L16.1647 20.9213C16.2688 20.9733 16.3836 21.0003 16.5 21.0003C16.6164 21.0003 16.7312 20.9733 16.8353 20.9213L21.3353 18.6713C21.4599 18.609 21.5648 18.5131 21.638 18.3946C21.7113 18.276 21.7501 18.1394 21.75 18V12.75C21.7501 12.6106 21.7113 12.474 21.638 12.3554C21.5648 12.2369 21.4599 12.141 21.3353 12.0787M15.75 10.0357L12.75 11.5357V7.96275L15.75 6.46275V10.0357ZM12 3.83775L14.823 5.25L12 6.6615L9.177 5.25L12 3.83775ZM8.25 6.46275L11.25 7.96275V11.5357L8.25 10.0357V6.46275ZM6.75 19.0365L3.75 17.5365V13.9635L6.75 15.4635V19.0365ZM7.5 14.1615L4.677 12.75L7.5 11.3385L10.323 12.75L7.5 14.1615ZM8.25 15.4635L11.25 13.9635V17.5365L8.25 19.0365V15.4635ZM15.75 19.0365L12.75 17.5365V13.9635L15.75 15.4635V19.0365ZM16.5 14.1615L13.677 12.75L16.5 11.3385L19.323 12.75L16.5 14.1615ZM20.25 17.5365L17.25 19.0365V15.4635L20.25 13.9635V17.5365Z"
                  />
                </SvgIcon>
              }
              iconPosition="start"
              label="AI Models"
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                minHeight: 48,
                color: "#212121",
                "&.Mui-selected": { color: "#2932E5" },
              }}
            />
          </Tabs>

          {/* Toolbar: Plan. < 1/10 > | Zoom out 100% Zoom in | Pan, Zoom to fit, Refresh, Zoom to selection, Info, Menu */}
          {activeTab === 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1,
                borderBottom: "1px solid #E0E0E0",
                flexWrap: "wrap",
                bgcolor: "#FAFAFA",
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 13,
                  color: "#4A5565",
                }}
              >
                Plan.
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <IconButton
                  size="small"
                  sx={{ p: 0.25, color: "#4A5565" }}
                  aria-label="Previous plan"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 13,
                    color: "#212121",
                    minWidth: 32,
                    textAlign: "center",
                  }}
                >
                  1/10
                </Typography>
                <IconButton
                  size="small"
                  sx={{ p: 0.25, color: "#4A5565" }}
                  aria-label="Next plan"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.25, ml: 1 }}
              >
                <IconButton
                  size="small"
                  sx={{ color: "#4A5565", p: 0.5 }}
                  aria-label="Zoom out"
                >
                  <ZoomOutIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: 13,
                    color: "#212121",
                    minWidth: 40,
                    textAlign: "center",
                  }}
                >
                  100%
                </Typography>
                <IconButton
                  size="small"
                  sx={{ color: "#4A5565", p: 0.5 }}
                  aria-label="Zoom in"
                >
                  <ZoomInIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                  ml: "auto",
                }}
              >
                <IconButton
                  size="small"
                  sx={{ color: "#4A5565", p: 0.5 }}
                  title="Pan"
                >
                  <PanToolIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ color: "#4A5565", p: 0.5 }}
                  title="Zoom to fit"
                >
                  <CropSquareIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ color: "#4A5565", p: 0.5 }}
                  title="Refresh"
                >
                  <RefreshIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ color: "#4A5565", p: 0.5 }}
                  title="Zoom to selection"
                >
                  <CropFreeIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ color: "#4A5565", p: 0.5 }}
                  title="Info"
                >
                  <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ color: "#4A5565", p: 0.5 }}
                  title="More options"
                >
                  <MoreVertIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {activeTab === 0 && <CameraLocationTab />}
            {activeTab === 1 && (
              <>
                {zonesByCameraLoading && (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading zones...
                    </Typography>
                  </Box>
                )}
                {!zonesByCameraLoading && zonesByCameraError && (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="error">
                      {zonesByCameraError}
                    </Typography>
                  </Box>
                )}
                {!zonesByCameraLoading && (
                  <ZonesTab
                    onDrawingStateChange={setIsDrawingMode}
                    zones={drawnZones}
                    onDeleteZone={handleDeleteZone}
                    onEditZone={handleEditZone}
                    zonesError={zonesByCameraError ?? undefined}
                    markZonesSurfaceReady={markZonesSurfaceReady}
                    canAddMoreZones={drawnZones.length < MAX_ZONES_PER_CAMERA}
                    maxZonesPerCamera={MAX_ZONES_PER_CAMERA}
                    selectedZoneId={
                      editingZone?.id ?? markZonesHighlightedZoneId
                    }
                    onZoneRowSelect={(z) => {
                      const currentId =
                        editingZone?.id ?? markZonesHighlightedZoneId;
                      if (currentId === z.id) {
                        if (editingZone?.id === z.id) {
                          handleCancelDrawing();
                        } else {
                          setMarkZonesHighlightedZoneId(null);
                        }
                        return;
                      }
                      setMarkZonesHighlightedZoneId(z.id);
                    }}
                    readOnly={readOnly}
                  />
                )}
              </>
            )}
            {activeTab === 2 && (
              <AIModelsTab
                zones={drawnZones}
                cameraId={effectiveCameraId}
                cameraAiConfigs={cameraAiConfigs}
                cameraAiConfigsLoading={cameraAiConfigsLoading}
                cameraAiConfigsError={cameraAiConfigsError}
                onConfigCreated={() => setConfigsRefreshKey((k) => k + 1)}
                cameraSyncPayload={cameraSyncPayloadForAiTab}
                onRequestZonesRefresh={() => {
                  void fetchZonesForCamera();
                }}
                readOnly={readOnly}
              />
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
